/** vendor */
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

/** lib */
import { useConfirm } from '../../hooks/useConfirm'
import { useToast } from '../../hooks/useToast'

/** state */
import { 
  getUserMealplan, 
  getSharedWithMe 
} from '../../actions/user.actions'

/** components */
import MealplanIngredientSummaryDialog from '../../components/Dialogs/MealplanIngredientSummaryDialog'
import { Card } from '../../components/UI/Card'
import Loading from '../../components/UI/Loading'

export default function FoodDiary() {
  // vendor utils
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  // a9 utils
  const confirm = useConfirm()
  const toast = useToast()

  // show / hide dialogs
  const [showOptions, setShowOptions] = useState(false)
  const [showFoodDiaryEntryDialog, setShowFoodDiaryEntryDialog] = useState(false)
  const [showIngredientSummaryDialog, setShowIngredientSummaryDialog] = useState(false)

  // ui states
  const [loading, setLoading] = useState(false)  

  // mealplans
  const mealplans = useSelector(state => state.user_data.mealplans)
  const sharedWithMeMealplans = useSelector(state => state.user_data.shared_with_me.filter(m => m.type === 'UserMealplan'))

  const [diary, setDiary] = useState([])

  const setStorage = (diary) => {
    localStorage.setItem('diary', diary);
  }

  const checkStorage = () => {
    const d = localStorage.getItem('diary')
    return d ? JSON.parse(d) : undefined
  }

  const checkIngredients = () => {

  }

  const checkMealPlans = () => {
    const storedDiary = checkStorage()

    const now = new Date();

    const allPlans = [
      ...(mealplans || []),
      ...(sharedWithMeMealplans || [])
    ]

    const allScheduledRecipes = allPlans.flatMap(plan =>
      plan.recipes.flatMap(day =>
        (day.recipes || []).map(recipe => ({
          ...recipe,
          date: new Date(day.date),
          mealplanId: plan.user_mealplan_id
        }))
      )
    )

    const pastRecipes = allScheduledRecipes.filter(r => r.date < now);

    const updatedDiary = storedDiary
      ? [...storedDiary, ...pastRecipes]
      : pastRecipes

    const uniqueEntries = Object.values(
      updatedDiary.reduce((acc, r) => {
        const key = `${r.mealplanId}-${r.date}`;
        acc[key] = r;
        return acc;
      }, {})
    )

    const sortedEntries = uniqueEntries.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    )

    setDiary(sortedEntries)
    setStorage(JSON.stringify(sortedEntries))
  }


  /**
   * Hooks
   */
  useEffect(() => {
    setLoading(true)
    checkMealPlans()
    checkIngredients()
    setLoading(false)
  }, [])

  /**
   * Handlers
   */
  const handleShowOptions = () => {
    setShowOptions(true)
  }

  const handleOnShowIngredientSummary = () => {
    setShowOptions(false)
    setShowIngredientSummaryDialog(true)
  }



  const handleOnAction = (action, id) => {
    // if (action === 'delete')  {
      
    //   confirm.open(
    //     `Delete ${mealplan?.mealplan?.name}?`,
    //     'This action cannot be undone',
    //     () => {
    //       setLoading(true)

    //       dispatch(deleteMealplan(id)).then(() => {
    //         toast.open('Meal Plan Deleted.') 
    //         navigate(`/user-meal-plans`) 
    //       }).catch(() => {
    //         setLoading(false)
    //       })
    //     }
    //   )
    // }

    if (action === 'check') {
        handleOnShowIngredientSummary()
    }

  }


  return (
    <div className="pt-12 md:max-w-4xl w-full">
        <Card title={'Diary'}
          status={
            <div className="flex">
              <button
                className="text-xs h-6 w-6 text-black rounded-full"
                onClick={ (e) => { handleShowOptions() } }
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                </svg>
              </button>
            </div>
          }
        >
            <div className="overflow-y-auto">
                {
                    loading && <div className="h-[60vh] relative pt-40"><Loading /></div>
                }

                {
                    !loading &&
                        <div>
                            <div className="text-center opacity-60 mt-8">                   
                                  {
                                    diary && diary?.length > 0 && diary.map((recipe, i) => {   
                                      let route = `/`

                                      const recipeDetails = undefined
                                      // const recipeDetails = (recipe?.type === 'A9' || !recipe?.type) 
                                      //   ? mealplan?.recipes.find((r) => {
                                      //       return r?.recipe_id === recipe?.value
                                      //     }) ?? undefined : undefined

                                      if (recipe?.type === 'A9' || !recipe?.type) route += `recipes`
                                      if (recipe?.type === 'IMPORTED') route += `imported-recipes`
                                      if (recipe?.type === 'USER') route += `user-recipes`

                                      route += `/${recipe?.value}`

                                      return (
                                        <div key={i} className={`even:bg-an_white-dark hover:bg-an_white-darker1 p-2 cursor-pointer`}>
                                            <small className="flex">{recipe?.date && recipe?.date.toLocaleString()}</small>

                                            {
                                              recipe?.type !== 'EXTERNAL_LINK' && !recipe?.value &&
                                                <div className="flex justify-between items-center py-2">
                                                  <div>
                                                    <label className="block">{recipe?.name}</label>
                                                    <Link to={`/recipe-creator?name=${recipe?.name}`}>
                                                      <small className="block text-xs font-light underline">
                                                        Create a new Recipe
                                                      </small>
                                                    </Link>
                                                  </div>
                                                
                                                  <div className="flex items-center">                            
                                                    <div className="flex py-2 items-center mr-2 w-4 h-4">
                                                    </div>
                                                  </div>
                                                </div>
                                            }

                                            {
                                              (
                                                recipe?.value && 
                                                recipe?.type !== 'EXTERNAL_LINK'
                                              ) &&
                                                <div onClick={(e) => {navigate(route)}} className="cursor-pointer">
                                                  <div className="flex justify-between items-center py-2" key={i}>
                                                    <div>
                                                      <label className="block">
                                                        {(recipe?.type === 'A9' || !recipe?.type) && recipe?.name}
                                                        {recipe?.type === 'IMPORTED' && recipe?.name}
                                                        {recipe?.type === 'USER' && recipe?.name}
                                                      </label>
                                                      <small className="block text-xs font-light">
                                                        {recipeDetails?.recipe_ingredients.length} ingredient(s)
                                                      </small>
                                                    </div>
                                                  
                                                  </div>
                                                </div>
                                            }

                                            {
                                              recipe?.type === 'EXTERNAL_LINK' &&
                                                <a href={recipe?.value} target="_blank" className="cursor-pointer block"  key={i}>
                                                  <label className="block underline cursor-pointer">
                                                    {recipe?.name}
                                                  </label>
                                                  <small className="block text-xs font-light">external link</small>
                                                </a>
                                            }
                                        </div>
                                      )
                                    })

                                  }
                                
                                
                        

                        








                            </div>
                        </div>
                }
            </div> 
        </Card>
 
        {/* <div className={`fixed bottom-0 left-0 w-screen px-2 pb-3 flex items-center justify-center ${mealsCompleted.length < 1 && 'opacity-50'}`}>         
            <div>
            <button
                disabled={true}
                onClick={() => { setShowFoodDiaryEntryDialog(true) }}
                type="button" 
                className={`rounded font-medium border border-black text-sm px-5 py-2
                text-white bg-black
                `}
            >
                add entry
            </button>
            </div>
        </div>  */}
        
        {/* <MealplanIngredientSummaryDialog 
          showDialog={showIngredientSummaryDialog} 
          title="Ingredient Summary" 
          mealplanId={mealplan?.mealplan?.user_mealplan_id}
          onClose={() => { setShowIngredientSummaryDialog(false) }}
        /> */}

        {
            showFoodDiaryEntryDialog && <></>
        }
    </div>
  )
}